<!-- src/views/ChangePasswordView.vue -->
<template>
  <div class="change-password-view">
    <div class="container">
      <div class="password-card">
        <div class="card-header">
          <h1>Changement de mot de passe</h1>
          <p class="subtitle">Veuillez saisir votre ancien mot de passe et choisir un nouveau mot de passe sécurisé</p>
        </div>
        
        <div class="card-body">
          <form @submit.prevent="changePassword" class="password-form">
            <!-- Ancien mot de passe -->
            <div class="form-group">
              <label for="old-password">Ancien mot de passe *</label>
              <div class="input-with-icon">
                <input 
                  :type="showOldPassword ? 'text' : 'password'" 
                  id="old-password" 
                  v-model="oldPassword" 
                  :class="{'has-error': !oldPassword && errorMessage}"
                  placeholder="Entrez votre ancien mot de passe"
                />
                <button 
                  type="button" 
                  class="toggle-password" 
                  @click="showOldPassword = !showOldPassword"
                >
                  <span class="material-icons">
                    {{ showOldPassword ? 'visibility_off' : 'visibility' }}
                  </span>
                </button>
              </div>
              <div class="error-message" v-if="!oldPassword && errorMessage">
                Veuillez saisir votre mot de passe actuel
              </div>
            </div>

            <!-- Nouveau mot de passe -->
            <div class="form-group">
              <label for="new-password">Nouveau mot de passe *</label>
              <div class="input-with-icon">
                <input 
                  :type="showNewPassword ? 'text' : 'password'" 
                  id="new-password" 
                  v-model="newPassword" 
                  :class="{'has-error': !passwordCriteria.length && errorMessage}"
                  placeholder="Entrez votre nouveau mot de passe"
                  @input="updateStrengthMeter"
                />
                <button 
                  type="button" 
                  class="toggle-password" 
                  @click="showNewPassword = !showNewPassword"
                >
                  <span class="material-icons">
                    {{ showNewPassword ? 'visibility_off' : 'visibility' }}
                  </span>
                </button>
              </div>
              <div class="error-message" v-if="errorMessage && !passwordCriteria.length">
                {{ errorMessage }}
              </div>
              
              <!-- Indicateur de force du mot de passe -->
              <div class="password-strength">
                <div class="strength-meter">
                  <div :class="strengthClass" :style="{ width: passwordStrength + '%' }"></div>
                </div>
                <span class="strength-text">{{ strengthText }}</span>
              </div>
              
              <!-- Règles de mot de passe -->
              <div class="password-rules">
                <h4>Votre mot de passe doit contenir:</h4>
                <ul>
                  <li :class="{ valid: passwordCriteria.length }">
                    <span class="material-icons">{{ passwordCriteria.length ? 'check_circle' : 'cancel' }}</span>
                    Au moins 8 caractères
                  </li>
                  <li :class="{ valid: passwordCriteria.uppercase }">
                    <span class="material-icons">{{ passwordCriteria.uppercase ? 'check_circle' : 'cancel' }}</span>
                    Au moins une lettre majuscule
                  </li>
                  <li :class="{ valid: passwordCriteria.lowercase }">
                    <span class="material-icons">{{ passwordCriteria.lowercase ? 'check_circle' : 'cancel' }}</span>
                    Au moins une lettre minuscule
                  </li>
                  <li :class="{ valid: passwordCriteria.number }">
                    <span class="material-icons">{{ passwordCriteria.number ? 'check_circle' : 'cancel' }}</span>
                    Au moins un chiffre
                  </li>
                  <li :class="{ valid: passwordCriteria.special }">
                    <span class="material-icons">{{ passwordCriteria.special ? 'check_circle' : 'cancel' }}</span>
                    Au moins un caractère spécial (!@#$%^&*(),.?":{}|&lt;&gt;)
                  </li>
                </ul>
              </div>
            </div>

            <!-- Confirmation du mot de passe -->
            <div class="form-group">
              <label for="confirm-password">Confirmation du mot de passe *</label>
              <div class="input-with-icon">
                <input 
                  :type="showConfirmPassword ? 'text' : 'password'" 
                  id="confirm-password" 
                  v-model="confirmPassword" 
                  :class="{'has-error': (newPassword !== confirmPassword) && errorMessage}"
                  placeholder="Confirmez votre nouveau mot de passe"
                />
                <button 
                  type="button" 
                  class="toggle-password" 
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <span class="material-icons">
                    {{ showConfirmPassword ? 'visibility_off' : 'visibility' }}
                  </span>
                </button>
              </div>
              <div class="error-message" v-if="(newPassword !== confirmPassword) && errorMessage">
                Les mots de passe ne correspondent pas
              </div>
            </div>

            <!-- Les messages d'erreur de validation seront toujours affichés au niveau des champs -->
            <div v-if="errorMessage && !isModalVisible" class="alert alert-danger">
              <span class="material-icons">error</span>
              {{ errorMessage }}
            </div>

            <!-- Actions -->
            <div class="form-actions">
              <button type="button" class="btn btn-outline" @click="goBack">Annuler</button>
              <button 
                type="submit" 
                class="btn btn-primary" 
                :disabled="isSubmitting || !formIsValid"
              >
                <span v-if="isSubmitting" class="spinner"></span>
                <span v-else>Changer mon mot de passe</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    
    <!-- Modale de dialogue pour les messages -->
    <div class="modal-overlay" v-if="isModalVisible" @click.self="closeModal">
      <div class="dialog-modal" :class="{ 'success-modal': isSuccess, 'error-modal': !isSuccess }">
        <div class="modal-header">
          <h3>{{ isSuccess ? 'Succès' : 'Erreur' }}</h3>
        </div>
        
        <div class="modal-content">
          <div class="modal-icon">
            <span class="material-icons">{{ isSuccess ? 'check_circle' : 'error' }}</span>
          </div>
          <p>{{ modalMessage }}</p>
        </div>
        
        <div class="modal-footer">
          <button 
            class="btn" 
            :class="{'btn-success': isSuccess, 'btn-danger': !isSuccess}"
            @click="closeModal"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import { useAuthStore } from '@/store/auth'

// Router
const router = useRouter()

// Auth store
const authStore = useAuthStore()

// États
const oldPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const showOldPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)
const errorMessage = ref('')
const isSubmitting = ref(false)
const passwordErrors = ref([])

// États pour la modale de dialogue
const isModalVisible = ref(false)
const isSuccess = ref(false)
const modalMessage = ref('')

// Critères du mot de passe
const passwordCriteria = reactive({
  length: false,
  uppercase: false,
  lowercase: false,
  number: false,
  special: false
})

// Force du mot de passe
const passwordStrength = ref(0)
const strengthClass = computed(() => {
  if (passwordStrength.value < 25) return 'strength-weak'
  if (passwordStrength.value < 50) return 'strength-fair'
  if (passwordStrength.value < 75) return 'strength-good'
  return 'strength-strong'
})

const strengthText = computed(() => {
  if (passwordStrength.value < 25) return 'Faible'
  if (passwordStrength.value < 50) return 'Moyen'
  if (passwordStrength.value < 75) return 'Bon'
  return 'Fort'
})

// Vérifier la validité du formulaire
const formIsValid = computed(() => {
  return oldPassword.value.length > 0 && 
         passwordCriteria.length && 
         passwordCriteria.uppercase && 
         passwordCriteria.lowercase && 
         passwordCriteria.number && 
         passwordCriteria.special &&
         newPassword.value === confirmPassword.value
})

// Mettre à jour l'indicateur de force du mot de passe
const updateStrengthMeter = () => {
  let strength = 0
  
  // Vérifier les critères
  passwordCriteria.length = newPassword.value.length >= 8
  passwordCriteria.uppercase = /[A-Z]/.test(newPassword.value)
  passwordCriteria.lowercase = /[a-z]/.test(newPassword.value)
  passwordCriteria.number = /[0-9]/.test(newPassword.value)
  passwordCriteria.special = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword.value)
  
  // Calculer la force
  if (newPassword.value.length >= 8) strength += 20
  if (/[A-Z]/.test(newPassword.value)) strength += 20
  if (/[a-z]/.test(newPassword.value)) strength += 20
  if (/[0-9]/.test(newPassword.value)) strength += 20
  if (/[!@#$%^&*(),.?":{}|<>]/.test(newPassword.value)) strength += 20
  
  passwordStrength.value = strength
}

// Validation manuelle du formulaire
const validateForm = () => {
  passwordErrors.value = []
  
  // Valider l'ancien mot de passe
  if (!oldPassword.value) {
    passwordErrors.value.push('Veuillez saisir votre mot de passe actuel')
  }
  
  // Valider le nouveau mot de passe
  if (!newPassword.value) {
    passwordErrors.value.push('Veuillez saisir un nouveau mot de passe')
  } else {
    // Vérifier que le nouveau mot de passe n'est pas identique à l'ancien
    if (newPassword.value === oldPassword.value) {
      passwordErrors.value.push("Le nouveau mot de passe ne peut pas être identique à l'ancien")
    }
    
    if (newPassword.value.length < 8) {
      passwordErrors.value.push('Le mot de passe doit contenir au moins 8 caractères')
    }
    if (!/[A-Z]/.test(newPassword.value)) {
      passwordErrors.value.push('Le mot de passe doit contenir au moins une lettre majuscule')
    }
    if (!/[a-z]/.test(newPassword.value)) {
      passwordErrors.value.push('Le mot de passe doit contenir au moins une lettre minuscule')
    }
    if (!/[0-9]/.test(newPassword.value)) {
      passwordErrors.value.push('Le mot de passe doit contenir au moins un chiffre')
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword.value)) {
      passwordErrors.value.push('Le mot de passe doit contenir au moins un caractère spécial')
    }
  }
  
  // Valider la confirmation du mot de passe
  if (!confirmPassword.value) {
    passwordErrors.value.push('Veuillez confirmer votre nouveau mot de passe')
  } else if (confirmPassword.value !== newPassword.value) {
    passwordErrors.value.push('Les mots de passe ne correspondent pas')
  }
  
  return passwordErrors.value.length === 0
}

// Changer le mot de passe
const changePassword = async () => {
  // Valider le formulaire
  if (!validateForm()) {
    errorMessage.value = passwordErrors.value[0] // Afficher la première erreur
    return
  }
  
  errorMessage.value = ''
  isSubmitting.value = true
  
  try {
    await api.changePassword({
      oldPassword: oldPassword.value,
      newPassword: newPassword.value
    })
    
    // Afficher la modale de succès
    isSuccess.value = true
    modalMessage.value = 'Votre mot de passe a été modifié avec succès.\nVous allez être déconnecté dans 3 secondes.'
    isModalVisible.value = true
    
    // Déconnecter l'utilisateur après 3 secondes pour laisser le temps de voir le message
    setTimeout(() => {
      authStore.logout()
      router.push('/login')
    }, 3000)
    
  } catch (error) {
    console.error('Erreur lors du changement de mot de passe:', error)
    
    // Déterminer le message d'erreur
    let message = 'Une erreur est survenue. Veuillez réessayer.'
    if (error.response?.status === 401) {
      message = 'Votre ancien mot de passe est incorrect.'
    } else if (error.response?.data?.message) {
      message = error.response.data.message
    }
    
    // Afficher la modale d'erreur pour les erreurs liées à l'API
    isSuccess.value = false
    modalMessage.value = message
    isModalVisible.value = true
    
    // Conserver aussi l'erreur pour l'affichage en bas du formulaire si nécessaire
    errorMessage.value = message
  } finally {
    isSubmitting.value = false
  }
}

// Retour à la page précédente
const goBack = () => {
  router.back()
}

// Fermer la modale
const closeModal = () => {
  isModalVisible.value = false
}
</script>

<style scoped>
.change-password-view {
  min-height: calc(100vh - 64px);
  background-color: #f8f9fa;
  padding: 40px 20px;
}

.container {
  max-width: 800px;
  margin: 0 auto;
}

.password-card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.card-header {
  padding: 30px 40px;
  background: linear-gradient(135deg, var(--turquoise-deep), var(--turquoise-primary));
  color: white;
}

.card-header h1 {
  margin: 0 0 10px;
  font-size: 24px;
  font-weight: 600;
}

.subtitle {
  opacity: 0.9;
  margin: 0;
  font-weight: 300;
  font-size: 16px;
}

.card-body {
  padding: 40px;
}

.password-form {
  max-width: 600px;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: var(--turquoise-very-dark);
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-with-icon input {
  width: 100%;
  padding: 12px 46px 12px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 15px;
  color: #333;
  transition: all 0.2s ease;
}

.input-with-icon input:focus {
  border-color: var(--turquoise-primary);
  outline: none;
  box-shadow: 0 0 0 2px rgba(48, 199, 181, 0.2);
}

.input-with-icon input.has-error {
  border-color: var(--coral-soft);
}

.toggle-password {
  position: absolute;
  right: 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-password .material-icons {
  font-size: 20px;
  color: var(--turquoise-primary);
}

.error-message {
  color: var(--coral-soft);
  font-size: 13px;
  margin-top: 6px;
  display: flex;
  align-items: center;
}

.error-message .material-icons {
  font-size: 16px;
  margin-right: 6px;
}

.password-strength {
  margin-top: 16px;
}

.strength-meter {
  height: 5px;
  background-color: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 6px;
}

.strength-meter > div {
  height: 100%;
  transition: width 0.3s ease;
}

.strength-weak {
  background-color: var(--coral-soft);
}

.strength-fair {
  background-color: var(--coral-soft);
}

.strength-good {
  background-color: var(--green-emerald);
}

.strength-strong {
  background-color: var(--turquoise-deep);
}

.strength-text {
  font-size: 12px;
  color: var(--turquoise-very-dark);
  text-align: right;
  display: block;
}

.password-rules {
  margin-top: 16px;
  padding: 16px;
  background-color: var(--turquoise-pale);
  border-radius: 6px;
}

.password-rules h4 {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 500;
  color: var(--turquoise-very-dark);
}

.password-rules ul {
  padding: 0;
  margin: 0;
  list-style: none;
}

.password-rules li {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  font-size: 13px;
  color: #757575;
}

.password-rules li .material-icons {
  font-size: 16px;
  margin-right: 8px;
  color: #9e9e9e;
}

.password-rules li.valid {
  color: #333;
}

.password-rules li.valid .material-icons {
  color: var(--turquoise-primary);
}

.alert {
  padding: 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.alert-danger {
  background-color: rgba(255, 122, 104, 0.15);
  color: var(--coral-soft);
}

.alert .material-icons {
  font-size: 20px;
  margin-right: 10px;
}

.form-actions {
  margin-top: 30px;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
}

.btn {
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.btn-outline {
  background-color: transparent;
  border: 1px solid #ddd;
  color: #555;
}

.btn-outline:hover {
  background-color: #f5f5f5;
}

.btn-primary {
  background-color: var(--turquoise-primary);
  color: white;
}

.btn-primary:hover {
  background-color: var(--turquoise-deep);
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Styles pour la modale de dialogue */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog-modal {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 450px;
  overflow: hidden;
  animation: modal-appear 0.3s ease-out forwards;
}

@keyframes modal-appear {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.success-modal .modal-header {
  background-color: var(--turquoise-pale);
  color: var(--turquoise-deep);
}

.error-modal .modal-header {
  background-color: #ffebee;
  color: var(--coral-soft);
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.modal-content {
  padding: 24px 20px;
  display: flex;
  align-items: center;
}

.modal-icon {
  margin-right: 16px;
}

.modal-icon .material-icons {
  font-size: 36px;
}

.success-modal .modal-icon .material-icons {
  color: var(--green-emerald);
}

.error-modal .modal-icon .material-icons {
  color: var(--coral-soft);
}

.modal-content p {
  margin: 0;
  white-space: pre-line;
  line-height: 1.5;
}

.modal-footer {
  padding: 16px 20px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #eee;
}

.btn-success {
  background-color: var(--green-emerald);
  color: white;
}

.btn-success:hover {
  background-color: var(--turquoise-deep);
}

.btn-danger {
  background-color: var(--coral-soft);
  color: white;
}

.btn-danger:hover {
  background-color: #FF6A58;
}

/* Responsive */
@media (max-width: 768px) {
  .change-password-view {
    padding: 20px 10px;
  }
  
  .card-header,
  .card-body {
    padding: 24px;
  }
  
  .form-actions {
    flex-direction: column-reverse;
  }
  
  .btn {
    width: 100%;
  }
}
</style>
